import { forwardRef, ButtonHTMLAttributes } from 'react'
import clsx from 'clsx'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  loadingText?: string
  fullWidth?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

/**
 * Brand-styled button component
 *
 * Primary: Serenity Teal (#006D77) - Main actions
 * Secondary: Outlined teal - Secondary actions
 * Success: Progress Mint (#83C5BE) - Positive actions
 * Ghost: Transparent - Tertiary actions
 * Danger: Muted red - Only for system actions, never financial
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      loadingText,
      fullWidth = false,
      leftIcon,
      rightIcon,
      className,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles = `
      inline-flex items-center justify-center gap-2
      font-outfit font-medium rounded-button
      transition-all duration-200 ease-brand
      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface
      disabled:opacity-50 disabled:cursor-not-allowed
    `

    const variants = {
      primary: `
        bg-accent text-on-accent shadow-button
        hover:bg-accent-hover active:bg-accent-hover
        focus-visible:ring-accent
      `,
      secondary: `
        bg-surface text-accent border border-hairline
        hover:bg-accent-soft active:bg-accent-soft
        focus-visible:ring-accent
      `,
      success: `
        bg-success-soft text-success
        hover:bg-success-soft active:bg-success-soft
        focus-visible:ring-success
      `,
      ghost: `
        bg-transparent text-ink-muted
        hover:bg-surface-alt active:bg-surface-alt
        focus-visible:ring-accent
      `,
      danger: `
        bg-danger-soft text-danger
        hover:opacity-90 active:opacity-90
        focus-visible:ring-danger
      `,
    }

    const sizes = {
      sm: 'min-h-11 px-4 py-2 text-body-sm',
      md: 'px-6 py-3 text-body-sm',
      lg: 'px-8 py-4 text-body',
    }

    return (
      <button
        ref={ref}
        className={clsx(
          baseStyles,
          variants[variant],
          sizes[size],
          fullWidth && 'w-full',
          className,
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <Spinner size={size === 'sm' ? 16 : 20} />
            <span>{loadingText ?? 'Loading…'}</span>
          </>
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

function Spinner({ size = 20 }: { size?: number }) {
  return (
    <svg
      className="animate-spin"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}
